import type { Permission } from "../../../api/access/accessModel";

/**
 * How the screens sort the thirteen permissions (accounts.md GS-2): five project categories,
 * and the installation's own. The record makes a new server permission fail the build until
 * it has its place here.
 */
export type PermissionCategory = "content" | "curation" | "releases" | "agents" | "management" | "installation";

const CATEGORY_OF: Readonly<Record<Permission, PermissionCategory>> = {
  view: "content",
  propose: "content",
  curate: "curation",
  release: "releases",
  server_keys: "agents",
  manage_people: "management",
  change_rules: "management",
  delete_project: "management",
  create_projects: "installation",
  manage_accounts: "installation",
  manage_server_keys: "installation",
  change_installation: "installation",
  restore_projects: "installation",
};

/** Every permission in screen order: the record's order. */
const ORDERED = Object.keys(CATEGORY_OF) as Permission[];

export interface CategoryPermissions {
  readonly category: PermissionCategory;
  readonly permissions: readonly Permission[];
}

const PROJECT_CATEGORIES: readonly PermissionCategory[] = ["content", "curation", "releases", "agents", "management"];

/** The project permissions by category, as a group screen and the access panel list them. */
export const PROJECT_PERMISSIONS_BY_CATEGORY: readonly CategoryPermissions[] = PROJECT_CATEGORIES.map((category) => ({
  category,
  permissions: ORDERED.filter((permission) => CATEGORY_OF[permission] === category),
}));

/** The installation's own permissions, as an installation group's screen lists them first. */
export const INSTALLATION_PERMISSIONS: readonly Permission[] = ORDERED.filter((permission) => CATEGORY_OF[permission] === "installation");

export function isInstallationPermission(permission: Permission): boolean {
  return CATEGORY_OF[permission] === "installation";
}

/** The permissions in screen order, for sorting lists the server sends in its own order. */
export function permissionRank(permission: Permission): number {
  return ORDERED.indexOf(permission);
}

/**
 * Switching on the server's keys spends money: only key managers give it, with a reason
 * (accounts.md GS-4). The server enforces it; the screen asks for the reason first.
 */
export const NEEDS_REASON: Permission = "server_keys";
